import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import calculateBusinessImage from "lib/calculate-business-image";
import BusinessCard from "components/FeaturedBusinesses/FeaturedBusinessCard";
import Link from "next/link";
import { escape } from "lodash";
import RightArrow from "components/icons/rightarrow";
import PageQuery from "components/PageQuery";
import dyanmicShortCodeText from "components/Contentful/utils";

const OuterWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 3rem 0;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1273px; // max-width of 1220px + 50px padding + 3px transparent border
  align-items: center;
  width: 100%;
`;

const Spacer = styled.div`
  display: inline-block;
  width: 25px;
`;

const CarouselScrollContanier = styled.div`
  display: flex;
  position: relative;
  max-width: 100vw;
  overflow-x: auto;
`;

const BusinessesCarousel = styled.ul`
  display: flex;
  min-height: 287px;
  padding: 0 0 0 23px;
  margin: 0;
`;

const LineBreak = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.themeColors.lightGrey};
  margin: 1.5em 0;
`;

const Header = styled.div`
  font-size: ${({ theme }) => theme.fontSize(5)};
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.2;
  padding: 0 1.5625rem;

  ${({ theme }) => theme.mq.md`
    margin-bottom: 3.875rem;
    padding: 0;
    font-size: ${theme.fontSize(6)};
    max-width: 36.1875rem;
  `};

  ${({ theme }) => theme.mq.lg`
    max-width: 100%;
  `};
`;

const LinkWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.themeColors.primary};
  font-size: ${({ theme }) => theme.fontSize(1)};
  line-height: 17px;
  margin-top: 10px;
  white-space: nowrap;
  transition: ${({ theme }) => theme.opacityTransition};
  :hover {
    cursor: pointer;
    opacity: 0.8;
  }
  svg {
    margin: 0.125rem 0 0.1875rem 0.5rem;
    width: 0.6rem;
    height: 0.6rem;
  }

  ${({ theme }) => theme.mq.lg`
    align-self: flex-end;
    margin-top: 0;
  `}
`;

const FeaturedBusinessBlock = ({
  blockData,
  cityName,
  coordinates,
  service,
}) => {
  const escapedCityName = escape(cityName);

  const searchTypeDict = {
    groomers: "grooming",
    trainers: "training",
    vets: "veterinary",
  };
  const searchType = searchTypeDict[service];

  const contentfulVarDict = {
    city: cityName,
    escapedCityName,
    lat: coordinates?.lat,
    lon: coordinates?.lon,
    serviceType: service,
    serviceTypeSearch: searchType,
  };

  const header = dyanmicShortCodeText({
    textToSearch: blockData?.title || "Local pet businesses",
    replaceDictLookup: contentfulVarDict,
  });
  const redirectTitle = dyanmicShortCodeText({
    textToSearch:
      blockData?.redirectTitle || "View all dog groomers in this area",
    replaceDictLookup: contentfulVarDict,
  });
  const redirect = dyanmicShortCodeText({
    textToSearch: blockData?.redirectLink || "/search",
    replaceDictLookup: contentfulVarDict,
  });
  const lat = coordinates?.lat;
  const lon = coordinates?.lon;
  const filterQuery = (sts) => {
    return {
      startDate: new Date().getTime(),
      s: sts,
      latitude: lat,
      longitude: lon,
    };
  };
  const imagePlaceholders = [];
  return (
    <PageQuery
      userType="vendor"
      query={{
        vendorBusinesses: {
          fields: [
            "addressPayload",
            "businessName",
            "friendlyName",
            "mediumImageUrl",
            "urlRegion",
            "urlLocality",
            "restricted",
          ],
          filter: {
            restricted: "false",
            withServiceTypeNameSearchWithinDistance: filterQuery(searchType),
          },
          paginate: { number: 1, size: 4 },
          include: {
            vendorBusinessOpenHours: {
              fields: [
                "dayOfWeek",
                "vendorBusinessId",
                "calcStartTime",
                "calcEndTime",
              ],
              filter: {
                onDayOfWeek: new Date().getDay(),
                vendorBusinessId: { fromContext: "vendorBusiness.id" },
              },
            },
            serviceTypes: {
              fields: ["name", "vendorBusinessId"],
              filter: {
                vendorBusinessId: { fromContext: "vendorBusiness.id" },
              },
            },
          },
        },
      }}
    >
      {({ data }) => {
        const businessList = data?.vendorBusinesses?.data || [];
        return (
          <OuterWrapper>
            <InnerContainer>
              <Header> {header}</Header>
              <CarouselScrollContanier>
                <BusinessesCarousel>
                  {businessList?.map((biz) => {
                    const businessImage = calculateBusinessImage(biz, imagePlaceholders);
                    imagePlaceholders.push(businessImage);
                    return (
                      <BusinessCard
                        key={biz.id}
                        business={biz}
                        businessImage={businessImage}
                      />
                    );
                  })}
                </BusinessesCarousel>
                <Spacer />
              </CarouselScrollContanier>
              <LineBreak />
              <LinkWrapper>
                <Link href={redirect}>
                  <StyledLink>
                    {redirectTitle}
                    <RightArrow />
                  </StyledLink>
                </Link>
              </LinkWrapper>
            </InnerContainer>
          </OuterWrapper>
        );
      }}
    </PageQuery>
  );
};

export default FeaturedBusinessBlock;

FeaturedBusinessBlock.propTypes = {
  blockData: PropTypes.shape({
    title: PropTypes.string,
    redirectTitle: PropTypes.string,
    redirectLink: PropTypes.string,
    location: PropTypes.shape({
      lat: PropTypes.number,
      lon: PropTypes.number,
    }),
    searchType: PropTypes.string,
    service: PropTypes.string,
  }),
  cityName: PropTypes.string,
  coordinates: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  service: PropTypes.string,
};

FeaturedBusinessBlock.defaultProps = {
  blockData: undefined,
  cityName: "",
  coordinates: {
    lat: 41.8502,
    lon: -87.6736,
  },
  service: "grooming",
};
